// Keep in sync with tools/fabricreader/lib/optical-alignment.js (a separate checkout).
export function installOpticalAlignment(fabric) {
  const prototype = fabric.Text.prototype;
  const originalOffset = prototype._getLineLeftOffset;
  const originalToObject = prototype.toObject;

  prototype.opticalAlignment = false;
  prototype.cacheProperties = [...prototype.cacheProperties, 'opticalAlignment'];
  prototype.stateProperties = [...prototype.stateProperties, 'opticalAlignment'];
  prototype.toObject = function(propertiesToInclude) {
    return originalToObject.call(this, [...(propertiesToInclude || []), 'opticalAlignment']);
  };

  prototype._getOpticalLineOffset = function(lineIndex) {
    if (!this.opticalAlignment || this.isCurved || this.type !== 'textbox'
      || (this.textAlign || 'left').split(',')[0] !== 'left') return 0;

    const line = this._textLines[lineIndex] || [];
    const firstVisible = line.findIndex(char => !/^\s$/.test(char));
    if (firstVisible === -1) return 0;

    this.getLineWidth(lineIndex);
    const bounds = this.__charBounds[lineIndex];
    const runs = [];
    let left = 0;
    let width = 0;
    let text = '';
    let style = this.getCompleteStyleDeclaration(lineIndex, 0);

    // Follow Fabric's rendering runs, including kerning at a style boundary.
    for (let i = 0; i < line.length; i++) {
      const bound = bounds[i];
      if (width === 0) {
        left += bound.kernedWidth - bound.width;
        width = bound.width;
      } else {
        width += bound.kernedWidth;
      }
      text += line[i];
      const next = i + 1 < line.length ? this.getCompleteStyleDeclaration(lineIndex, i + 1) : null;
      const styleChanged = next && (this._hasStyleChanged
        ? this._hasStyleChanged(style, next)
        : fabric.util.hasStyleChanged(style, next, false));
      if (!next || this.charSpacing || styleChanged) {
        runs.push({ text, left, font: this._getFontDeclaration(style) });
        left += width;
        width = 0;
        text = '';
        style = next;
      }
    }

    const signature = JSON.stringify(runs);
    const cached = this._opticalLineCache?.[lineIndex];
    if (cached?.bounds === bounds && cached.signature === signature) return cached.offset;

    const context = this.getMeasuringContext();
    context.save();
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    let inkLeft = Infinity;
    for (const run of runs) {
      if (!run.text.trim()) continue;
      context.font = run.font;
      const bearing = context.measureText(run.text).actualBoundingBoxLeft;
      // node-canvas reports the left bearing with the opposite sign to browser Canvas.
      if (Number.isFinite(bearing)) inkLeft = Math.min(inkLeft, run.left + (fabric.isLikelyNode ? bearing : -bearing));
    }
    context.restore();

    // Keep intentional leading whitespace; remove only the glyph side bearing.
    const offset = Number.isFinite(inkLeft) ? bounds[firstVisible].left - inkLeft : 0;
    this._opticalLineCache ||= {};
    this._opticalLineCache[lineIndex] = { bounds, signature, offset };
    return offset;
  };

  // Fabric uses this offset for rendering, the cursor, selection and pointer hit testing.
  prototype._getLineLeftOffset = function(lineIndex) {
    return originalOffset.call(this, lineIndex) + this._getOpticalLineOffset(lineIndex);
  };
}
